import { useEffect } from 'react';
import logger from 'ws-scripts/modules/logger';
import { useFetchLocationGeoZip } from './useFetchLocationGeoZip';
import { useLocationsData } from './useLocationsData';

const LOG_PREFIX = '[ws-locations-control][useLocationSearch]';
const LOCATION_SEARCH_RANGE = 50;
const EXCLUDE_BY_RANGE = true;

/**
 * Hook that orchestrates geo-zip fetching and location search.
 * Responsibilities:
 * - Fetches geo-zip from a place ID via useFetchLocationGeoZip
 * - Manages location data lifecycle via useLocationsData
 * - Triggers search params updates when data is ready
 *
 * @param {Object} params
 * @param {boolean} params.isApiReady - Whether the Google Maps API is ready.
 * @param {object|null} params.sessionToken - The active AutocompleteSessionToken.
 * @param {Function} params.resetSession - Function to reset/end the session.
 * @param {string|null} params.selectedPlaceId - The selected Google Place ID.
 * @returns {{ locationCount: number, error: string|null, isLoading: boolean }}
 */
export const useLocationSearch = ({
	isApiReady,
	sessionToken,
	resetSession,
	selectedPlaceId
}) => {
	// Fetch geo-zip from place ID
	const {
		zipcode: geoZip,
		isLoading: geoLoading,
		error: geoError
	} = useFetchLocationGeoZip(
		isApiReady,
		sessionToken,
		resetSession,
		selectedPlaceId
	);

	// Manage location data
	const {
		locationCount,
		error: locationError,
		isLoading: locationLoading,
		isReady: isLocationsDataReady,
		setSearchParams
	} = useLocationsData();

	logger.debug(`${LOG_PREFIX} State`, {
		isReady: isLocationsDataReady,
		count: locationCount,
		geoZip
	});

	// Update search params when ready
	useEffect(() => {
		// Only call setSearchParams when:
		// 1. Location data is ready
		// 2. Not currently loading geo data
		// 3. Either no place selected (use default) OR geoZip is available (fetch completed)

		// No user-initiated search → use the default tile count
		const useDefaultTileCount = selectedPlaceId === null;
		// In default view, geoZip is not required as setSearchParams uses defaultPostalCode
		const hasValidGeoData = useDefaultTileCount || geoZip !== null;

		if (isLocationsDataReady && !geoLoading && hasValidGeoData) {
			setSearchParams({
				postalCode: geoZip,
				range: LOCATION_SEARCH_RANGE,
				excludeByRange: EXCLUDE_BY_RANGE,
				useDefaultTileCount
			});
		}
	}, [
		isLocationsDataReady,
		geoZip,
		setSearchParams,
		selectedPlaceId,
		geoLoading
	]);

	const error = geoError || locationError;
	const isLoading = geoLoading || locationLoading;

	return {
		locationCount,
		error,
		isLoading
	};
};
